import { useEffect, useMemo, useState } from "react";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ListMusic } from "lucide-react";
import { toast } from "sonner";

/**
 * Quick action: add a single media item to one or more playlists.
 * - Pre-checks playlists that already contain the media.
 * - Saving only modifies the playlists that changed (no overwrite of others).
 */
export default function AddToPlaylistsDialog({ open, onClose, media, playlists, onSaved }) {
  const initialIds = useMemo(() => {
    if (!media) return new Set();
    return new Set(playlists.filter((p) => (p.media_ids || []).includes(media.id)).map((p) => p.id));
  }, [media, playlists]);

  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSelected(new Set(initialIds));
  }, [open, initialIds]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    if (!media) return;
    setSaving(true);
    try {
      // Compute which playlists to add to and which to remove from
      const toAdd = [...selected].filter((id) => !initialIds.has(id));
      const toRemove = [...initialIds].filter((id) => !selected.has(id));
      const ops = [];
      for (const pid of toAdd) {
        const pl = playlists.find((p) => p.id === pid);
        if (!pl) continue;
        const newIds = [...(pl.media_ids || []), media.id];
        ops.push(apiAdmin.patch(`/playlists/${pid}`, { media_ids: newIds }));
      }
      for (const pid of toRemove) {
        const pl = playlists.find((p) => p.id === pid);
        if (!pl) continue;
        const newIds = (pl.media_ids || []).filter((mid) => mid !== media.id);
        ops.push(apiAdmin.patch(`/playlists/${pid}`, { media_ids: newIds }));
      }
      await Promise.all(ops);
      toast.success(
        toAdd.length || toRemove.length
          ? `Updated ${toAdd.length + toRemove.length} playlist${toAdd.length + toRemove.length > 1 ? "s" : ""}`
          : "No changes"
      );
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-to-playlists-dialog">
        <DialogHeader>
          <DialogTitle>Add to playlists</DialogTitle>
          <DialogDescription>
            Pick the playlists that should include <b>{media?.filename}</b>. Existing items in those playlists are
            kept as-is — this file is appended at the end.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 mt-2 max-h-72 overflow-y-auto">
          {playlists.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">
              No playlists yet — create one first.
            </div>
          )}
          {playlists.map((p) => {
            const checked = selected.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`w-full flex items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  checked
                    ? "border-blue-600 bg-blue-50"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid={`add-to-playlist-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-lg bg-violet-50 text-violet-600 grid place-items-center shrink-0">
                    <ListMusic className="h-4 w-4" strokeWidth={1.25} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{(p.media_ids || []).length} items</div>
                  </div>
                </div>
                <div
                  className={`h-5 w-5 rounded-full grid place-items-center ${
                    checked ? "bg-blue-600 text-white" : "border border-slate-300"
                  }`}
                >
                  {checked && <Check className="h-3 w-3" strokeWidth={2.5} />}
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-to-playlists-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-to-playlists-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
